import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Inbox, Send, FileText, Trash2, AlertOctagon, Archive, Folder, PenSquare, RefreshCw, Search, Star, Paperclip, CalendarDays,
  AlarmClock, Users, Settings2, Mail, MailOpen, FolderInput, ChevronLeft, ChevronRight, Loader2, X, Video, CalendarCheck, CalendarPlus,
  Check, ArrowLeft, HardDrive,
} from 'lucide-react';
import { email, quando, dataHora, diaCurto, nomeDe } from './api';
import { supabase } from '../supabase';
import { useTemaTela, SeletorTema, textoPara } from '../components/TemaTela';
import Conversa from './Conversa';
import Compor from './Compor';
import Caixas from './Caixas';
import './email.css';

const ICONE_PASTA = { '\\Inbox': Inbox, '\\Sent': Send, '\\Drafts': FileText, '\\Trash': Trash2, '\\Junk': AlertOctagon, '\\Archive': Archive };
const ORIGEM = { iw: 'IW', captacao: 'Captação' };
const assuntoBase = s => String(s || '').replace(/^\s*((re|res|enc|fw|fwd|tr|rv|r\d*)\s*:\s*)+/gi, '').replace(/\s+/g, ' ').trim().toLowerCase();

function useLargura() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => { const f = () => setW(window.innerWidth); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  return w;
}

// ── Lista de e-mails de uma pasta ───────────────────────────────────────────────
function ListaPasta({ conta, pasta, pastas, ehEnviados, aberto, onAbrir, recarregarSinal, onAviso, onContagem }) {
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtros, setFiltros] = useState({ naoLidas: false, sinalizados: false, noTexto: false });
  const [pagina, setPagina] = useState(0);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [sel, setSel] = useState(new Set());
  const [moverAberto, setMoverAberto] = useState(false);

  const carregar = useCallback((silencioso) => {
    if (!silencioso) setCarregando(true);
    setErro('');
    return email('lista', { contaId: conta.id, pasta, pagina, busca: buscaAplicada, ...filtros })
      .then(d => { setDados(d); setSel(new Set()); })
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [conta.id, pasta, pagina, buscaAplicada, filtros]);

  useEffect(() => { setPagina(0); setBusca(''); setBuscaAplicada(''); setDados(null); }, [conta.id, pasta]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { if (recarregarSinal) carregar(true); }, [recarregarSinal]); // eslint-disable-line react-hooks/exhaustive-deps

  // a tela atualiza um item (lido, sinalizado, removido) sem recarregar tudo
  useEffect(() => {
    onContagem.current = (como, uid) => setDados(d => {
      if (!d) return d;
      if (como === 'removido') return { ...d, total: d.total - 1, itens: d.itens.filter(i => i.uid !== uid) };
      if (como === 'pacientes') { carregar(true); return d; }
      return { ...d, itens: d.itens.map(i => i.uid !== uid ? i : { ...i, lido: como === 'lido' ? true : como === 'naoLido' ? false : i.lido, sinalizado: como === 'sinalizar' ? true : como === 'tirarSinal' ? false : i.sinalizado }) };
    });
  }, [onContagem, carregar]);

  async function emLote(como, destino) {
    const uids = [...sel];
    if (!uids.length) return;
    setMoverAberto(false);
    try {
      if (como === 'excluir') await email('mover', { contaId: conta.id, pasta, uids, papel: '\\Trash' });
      else if (como === 'mover') await email('mover', { contaId: conta.id, pasta, uids, destino });
      else await email('marcar', { contaId: conta.id, pasta, uids, como });
      onAviso(`${uids.length} e-mail(s) ${{ excluir: 'excluído(s)', mover: 'movido(s)', lido: 'marcado(s) como lido', naoLido: 'marcado(s) como não lido', sinalizar: 'sinalizado(s)', tirarSinal: 'sem sinal' }[como]}.`);
      carregar(true);
    } catch (e) { onAviso(e.message, 'erro'); }
  }

  const total = dados?.total || 0;
  // conversas da página: e-mails com o mesmo assunto (sem Re:/Enc:) viram uma linha só, como no Gmail
  const grupos = [];
  const porAssunto = new Map();
  for (const it of dados?.itens || []) {
    const k = assuntoBase(it.assunto) || `u${it.uid}`;
    if (porAssunto.has(k)) porAssunto.get(k).push(it);
    else { const g = [it]; porAssunto.set(k, g); grupos.push(g); }
  }
  const paginas = Math.max(1, Math.ceil(total / (dados?.porPagina || 50)));
  const todos = dados?.itens?.length && dados.itens.every(i => sel.has(i.uid));

  return (
    <div className="em-lista">
      <form className="em-busca" onSubmit={e => { e.preventDefault(); setPagina(0); setBuscaAplicada(busca.trim()); }}>
        <Search size={15} />
        <input placeholder="Buscar por assunto, remetente ou destinatário" value={busca} onChange={e => setBusca(e.target.value)} />
        {buscaAplicada && <button type="button" className="em-icone" onClick={() => { setBusca(''); setBuscaAplicada(''); }}><X size={14} /></button>}
      </form>
      <div className="em-filtros">
        {[['naoLidas', 'Não lidas'], ['sinalizados', 'Sinalizados'], ['noTexto', 'Buscar também no texto']].map(([k, r]) => (
          <button key={k} className={filtros[k] ? 'ativa' : ''} onClick={() => { setPagina(0); setFiltros(f => ({ ...f, [k]: !f[k] })); }}>{r}</button>
        ))}
        <button className="em-icone em-dir" title="Recarregar" onClick={() => carregar()}><RefreshCw size={14} className={carregando ? 'em-gira' : ''} /></button>
      </div>
      {sel.size > 0 && (
        <div className="em-lote">
          <span>{sel.size} selecionado(s)</span>
          <button className="em-icone" title="Marcar como lido" onClick={() => emLote('lido')}><MailOpen size={16} /></button>
          <button className="em-icone" title="Marcar como não lido" onClick={() => emLote('naoLido')}><Mail size={16} /></button>
          <button className="em-icone" title="Sinalizar" onClick={() => emLote('sinalizar')}><Star size={16} /></button>
          <div className="em-rel">
            <button className="em-icone" title="Mover" onClick={() => setMoverAberto(a => !a)}><FolderInput size={16} /></button>
            {moverAberto && <div className="em-menu">{pastas.filter(p => p.caminho !== pasta).map(p => <button key={p.caminho} style={{ paddingLeft: 10 + p.nivel * 12 }} onClick={() => emLote('mover', p.caminho)}>{p.nome}</button>)}</div>}
          </div>
          <button className="em-icone em-perigo" title="Excluir" onClick={() => emLote('excluir')}><Trash2 size={16} /></button>
          <button className="em-icone em-dir" title="Limpar seleção" onClick={() => setSel(new Set())}><X size={16} /></button>
        </div>
      )}
      {erro && <div className="em-aviso em-aviso-erro">{erro}</div>}
      <div className="em-itens">
        {!dados && carregando && <div className="em-vazio"><Loader2 size={16} className="em-gira" /> Carregando…</div>}
        {dados && !dados.itens.length && <div className="em-vazio">{buscaAplicada || filtros.naoLidas || filtros.sinalizados ? 'Nada encontrado.' : 'Pasta vazia.'}</div>}
        {grupos.length > 0 && (
          <label className="em-sel-todos"><input type="checkbox" checked={!!todos} onChange={() => setSel(todos ? new Set() : new Set(dados.itens.map(i => i.uid)))} /> Selecionar a página</label>
        )}
        {grupos.map(g => {
          const i = g[0]; // o mais recente da conversa
          const nomes = [...new Set(g.map(x => (ehEnviados ? (x.para || []).map(nomeDe).join(', ') : (x.de || []).map(p => (p.nome || p.email).split(/\s+/)[0]).join(', '))))];
          const pessoa = ehEnviados ? `Para: ${nomes.join(', ')}` : nomes.slice(0, 3).join(', ');
          const naoLido = g.some(x => !x.lido), sinal = g.some(x => x.sinalizado), anexo = g.some(x => x.anexo);
          const uids = g.map(x => x.uid);
          const marcado = uids.every(u => sel.has(u));
          const pacs = [...new Map(g.flatMap(x => x.indice?.pacientes || []).map(p => [p.nome.trim().toUpperCase(), p])).values()];
          return (
            <div key={i.uid} className={`em-item ${naoLido ? 'em-nao-lido' : ''} ${marcado ? 'ativo' : ''}`} onClick={() => onAbrir({ contaId: conta.id, pasta, uid: i.uid, lido: !naoLido })}>
              <input type="checkbox" checked={marcado} onClick={e => e.stopPropagation()} onChange={() => setSel(s => { const n = new Set(s); uids.forEach(u => (marcado ? n.delete(u) : n.add(u))); return n; })} />
              <button className={`em-item-estrela ${sinal ? 'em-estrela' : ''}`} title={sinal ? 'Tirar sinal' : 'Sinalizar'}
                onClick={async e => { e.stopPropagation(); try { await email('marcar', { contaId: conta.id, pasta, uids, como: sinal ? 'tirarSinal' : 'sinalizar' }); carregar(true); } catch (er) { onAviso(er.message, 'erro'); } }}>
                <Star size={16} fill={sinal ? 'currentColor' : 'none'} />
              </button>
              <span className="em-item-de" title={pessoa}>{pessoa || '(sem remetente)'}{g.length > 1 && <span className="em-item-qtd">{g.length}</span>}</span>
              <span className="em-item-texto">
                {pacs.slice(0, 2).map(p => <span key={p.id} className={`em-pac-mini ${p.confianca === 'verificar' && !p.manual ? 'em-pac-verificar' : ''}`}>{p.nome}</span>)}
                <span className="em-item-assunto">{i.assunto}</span>
                {i.resumo && <span className="em-item-resumo"> – {i.resumo}</span>}
              </span>
              <span className="em-item-icones">
                {anexo && <Paperclip size={14} />}
                {(g.some(x => x.convite) || g.some(x => ['convite', 'reuniao'].includes(x.indice?.tipo))) && <Video size={14} />}
                {g.some(x => x.indice?.naAgenda) && <CalendarCheck size={14} />}
                {g.some(x => x.indice?.prazos > 0) && <AlarmClock size={14} className="em-cor-prazo" />}
              </span>
              <button className="em-item-lido" title={naoLido ? 'Marcar como lido' : 'Marcar como não lido'}
                onClick={async e => { e.stopPropagation(); try { await email('marcar', { contaId: conta.id, pasta, uids, como: naoLido ? 'lido' : 'naoLido' }); carregar(true); } catch (er) { onAviso(er.message, 'erro'); } }}>
                {naoLido ? <MailOpen size={15} /> : <Mail size={15} />}
              </button>
              <span className="em-item-data">{quando(i.data)}</span>
            </div>
          );
        })}
      </div>
      {total > 0 && (
        <div className="em-paginacao">
          <span>{total.toLocaleString('pt-BR')} e-mail(s)</span>
          <button className="em-icone" disabled={pagina === 0} onClick={() => setPagina(p => p - 1)}><ChevronLeft size={16} /></button>
          <span>{pagina + 1} / {paginas}</span>
          <button className="em-icone" disabled={pagina + 1 >= paginas} onClick={() => setPagina(p => p + 1)}><ChevronRight size={16} /></button>
        </div>
      )}
    </div>
  );
}

// ── Reuniões (convites e reuniões citadas) ──────────────────────────────────────
function Reunioes({ eu, onAbrir, onAviso, sinal }) {
  const [d, setD] = useState(null);
  const carregar = () => email('reunioes').then(setD).catch(e => onAviso(e.message, 'erro'));
  useEffect(() => { carregar(); }, [sinal]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="em-vazio"><Loader2 size={16} className="em-gira" /> Carregando reuniões…</div>;
  const hoje = d.hoje;
  const grupos = [
    ['Hoje', d.reunioes.filter(r => r.convite?.data === hoje)],
    ['Próximas', d.reunioes.filter(r => r.convite?.data > hoje)],
    ['Já passaram (últimos 45 dias)', d.reunioes.filter(r => r.convite?.data < hoje).reverse()],
  ];
  async function porNaAgenda(r) {
    try { await email('paraAgenda', { indiceId: r.indiceId, dados: { titulo: r.convite.titulo, data: r.convite.data, horario: r.convite.horario || '09:00', horarioFim: r.convite.horarioFim, link: r.convite.link } }); onAviso('Posta na sua agenda.'); carregar(); }
    catch (e) { onAviso(e.message, 'erro'); }
  }
  return (
    <div className="em-painel">
      <div className="em-painel-cab"><h3><CalendarDays size={17} /> Reuniões</h3><span className="em-sub">Convites recebidos e reuniões citadas nos e-mails. Convites entram sozinhos na agenda de quem é da caixa e dos participantes que usam o Quality.</span></div>
      {!d.reunioes.length && <div className="em-vazio">Nenhuma reunião nos e-mails lidos pelo robô.</div>}
      {grupos.filter(([, l]) => l.length).map(([titulo, l]) => (
        <div key={titulo}>
          <div className="em-secao">{titulo} <span>{l.length}</span></div>
          {l.map(r => (
            <div key={r.indiceId} className={`em-linha ${r.tipo === 'cancelamento' ? 'em-cancelada' : ''}`} onClick={() => onAbrir({ contaId: r.contaId, pasta: r.pasta, uid: r.uid, messageId: r.messageId, lido: true })}>
              <div className="em-linha-data"><strong>{diaCurto(r.convite?.data)}</strong><span>{r.convite?.diaInteiro ? 'dia todo' : r.convite?.horario}</span></div>
              <div className="em-linha-corpo">
                <strong>{r.convite?.titulo || r.assunto}</strong>
                <span className="em-sub">{r.de} · recebido {quando(r.recebidoEm)}{r.convite?.repete ? ' · repete' : ''}</span>
                <div className="em-tags">
                  {r.tipo === 'cancelamento' && <span className="em-tag em-tag-vermelha">Cancelada</span>}
                  {r.tipo === 'reuniao' && <span className="em-tag em-tag-amarela">Citada no texto (sem convite)</span>}
                  {r.naMinhaAgenda ? <span className="em-tag em-tag-verde"><CalendarCheck size={12} /> Na sua agenda</span> : r.agenda.length > 0 && <span className="em-tag">Na agenda de {r.agenda.join(', ')}</span>}
                </div>
              </div>
              <div className="em-linha-acoes" onClick={e => e.stopPropagation()}>
                {r.convite?.link && r.tipo !== 'cancelamento' && <a className="em-btn em-btn-mini em-btn-primario" href={r.convite.link} target="_blank" rel="noopener noreferrer"><Video size={13} /> Entrar</a>}
                {!r.naMinhaAgenda && r.tipo !== 'cancelamento' && r.convite?.data >= hoje && <button className="em-btn em-btn-mini" onClick={() => porNaAgenda(r)}><CalendarPlus size={13} /> Agenda</button>}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Prazos citados nos e-mails ─────────────────────────────────────────────────
function Prazos({ onAbrir, onAviso, onAbrirPaciente, sinal }) {
  const [d, setD] = useState(null);
  const [verFeitos, setVerFeitos] = useState(false);
  const carregar = () => email('prazos').then(setD).catch(e => onAviso(e.message, 'erro'));
  useEffect(() => { carregar(); }, [sinal]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="em-vazio"><Loader2 size={16} className="em-gira" /> Carregando prazos…</div>;
  const hoje = d.hoje;
  const semana = new Date(Date.parse(`${hoje}T12:00:00Z`) + 7 * 86400000).toISOString().slice(0, 10);
  const abertos = d.prazos.filter(p => !p.feito);
  const grupos = [
    ['Vencidos', abertos.filter(p => p.data < hoje), 'vencido'],
    ['Hoje', abertos.filter(p => p.data === hoje), 'hoje'],
    ['Próximos 7 dias', abertos.filter(p => p.data > hoje && p.data <= semana), 'semana'],
    ['Depois', abertos.filter(p => p.data > semana), ''],
  ];
  async function marcar(p, feito) {
    try { await email('prazoMarcar', { indiceId: p.indiceId, data: p.data, feito }); carregar(); } catch (e) { onAviso(e.message, 'erro'); }
  }
  async function agenda(p) {
    try { await email('paraAgenda', { indiceId: p.indiceId, prazo: true, dados: { titulo: `Prazo: ${p.assunto}`, data: p.data, horario: '09:00', horarioFim: '09:30' } }); onAviso(`Prazo posto na sua agenda (${diaCurto(p.data)}, 09:00).`); }
    catch (e) { onAviso(e.message, 'erro'); }
  }
  const linha = (p, cls) => (
    <div key={`${p.indiceId}-${p.data}`} className={`em-linha em-prazo-${cls || 'normal'}`} onClick={() => onAbrir({ contaId: p.contaId, pasta: p.pasta, uid: p.uid, messageId: p.messageId, lido: true })}>
      <div className="em-linha-data"><strong>{diaCurto(p.data)}</strong></div>
      <div className="em-linha-corpo">
        <strong>{p.assunto}</strong>
        <span className="em-sub">{p.de} · recebido {quando(p.recebidoEm)}</span>
        <span className="em-trecho">“…{p.trecho}…”</span>
        {p.pacientes?.length > 0 && <div className="em-tags">{p.pacientes.map(x => <button key={x.id} className="em-pac-mini" onClick={e => { e.stopPropagation(); onAbrirPaciente(x); }}>{x.nome}</button>)}</div>}
      </div>
      <div className="em-linha-acoes" onClick={e => e.stopPropagation()}>
        <button className="em-btn em-btn-mini" onClick={() => marcar(p, !p.feito)}><Check size={13} /> {p.feito ? 'Reabrir' : 'Resolvido'}</button>
        {!p.feito && <button className="em-btn em-btn-mini" onClick={() => agenda(p)}><CalendarPlus size={13} /> Agenda</button>}
      </div>
    </div>
  );
  const feitos = d.prazos.filter(p => p.feito);
  return (
    <div className="em-painel">
      <div className="em-painel-cab"><h3><AlarmClock size={17} /> Prazos</h3><span className="em-sub">Datas-limite encontradas no texto dos e-mails recebidos ("até 25/10", "em 48 horas", "5 dias úteis"…). Confira no e-mail e marque como resolvido.</span></div>
      {!abertos.length && <div className="em-vazio">Nenhum prazo em aberto. 🎉</div>}
      {grupos.filter(([, l]) => l.length).map(([titulo, l, cls]) => (
        <div key={titulo}><div className={`em-secao em-secao-${cls}`}>{titulo} <span>{l.length}</span></div>{l.map(p => linha(p, cls))}</div>
      ))}
      {feitos.length > 0 && <button className="em-link-copias" onClick={() => setVerFeitos(v => !v)}>{verFeitos ? 'Esconder' : 'Ver'} resolvidos ({feitos.length})</button>}
      {verFeitos && feitos.map(p => linha(p, 'feito'))}
    </div>
  );
}

// ── Pacientes: "pasta" de cada paciente ─────────────────────────────────────────
function Pacientes({ paciente, setPaciente, onAbrir, onAviso, aberto }) {
  const [busca, setBusca] = useState('');
  const [d, setD] = useState(null);
  const [emails, setEmails] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => email('pacientes', { busca }).then(setD).catch(e => onAviso(e.message, 'erro')), busca ? 350 : 0);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!paciente) { setEmails(null); return; }
    setEmails(null);
    email('pacienteEmails', { pacienteIds: paciente.ids || [paciente.id] }).then(r => setEmails(r.emails)).catch(e => onAviso(e.message, 'erro'));
  }, [paciente]); // eslint-disable-line react-hooks/exhaustive-deps

  if (paciente) {
    return (
      <div className="em-painel">
        <div className="em-painel-cab">
          <button className="em-voltar" onClick={() => setPaciente(null)}><ArrowLeft size={15} /> Pacientes</button>
          <h3><Users size={17} /> {paciente.nome}</h3>
          <span className="em-sub">{(paciente.origens || [paciente.origem]).map(o => ORIGEM[o]).join(' + ')} · e-mails ligados a este paciente (achados pelo nome ou ligados à mão). Continuam também na caixa de entrada.</span>
        </div>
        {!emails && <div className="em-vazio"><Loader2 size={16} className="em-gira" /></div>}
        {emails?.length === 0 && <div className="em-vazio">Nenhum e-mail ligado ainda. Abra um e-mail e use “+ Paciente”.</div>}
        {emails?.map(m => {
          const pac = (m.pacientes || []).find(x => (paciente.ids || [paciente.id]).includes(x.id)) || {};
          return (
            <div key={m.id} className={`em-linha ${aberto?.messageId === m.message_id ? 'ativo' : ''}`} onClick={() => onAbrir({ contaId: m.conta_id, pasta: m.pasta, uid: m.uid, messageId: m.message_id, lido: true })}>
              <div className="em-linha-data"><strong>{quando(m.recebido_em)}</strong></div>
              <div className="em-linha-corpo">
                <strong>{m.assunto || '(sem assunto)'}</strong>
                <span className="em-sub">{m.de_nome || m.de_email}{/Enviad|Sent/i.test(m.pasta) ? ' (enviado)' : ''}</span>
                <span className="em-trecho">{m.resumo}</span>
                <div className="em-tags">
                  {pac.confianca === 'verificar' && !pac.manual && <span className="em-tag em-tag-amarela">Verificar se é este paciente</span>}
                  {m.tem_anexo && <span className="em-tag"><Paperclip size={11} /> anexo</span>}
                  {(m.prazos || []).some(p => !p.feito) && <span className="em-tag em-tag-vermelha"><AlarmClock size={11} /> prazo</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="em-painel">
      <div className="em-painel-cab">
        <h3><Users size={17} /> Pacientes</h3>
        <span className="em-sub">O robô procura o nome completo dos pacientes do IW (em atendimento ou com movimento no último ano) e dos cards da Captação em cada e-mail novo.</span>
      </div>
      <div className="em-busca"><Search size={15} /><input placeholder="Buscar paciente" value={busca} onChange={e => setBusca(e.target.value)} /></div>
      {!d && <div className="em-vazio"><Loader2 size={16} className="em-gira" /></div>}
      {d && !d.comEmails.length && !d.encontrados.length && <div className="em-vazio">{busca ? 'Ninguém encontrado.' : 'Ainda não há e-mails ligados a pacientes.'}</div>}
      {d?.comEmails.map(p => (
        <div key={p.id} className="em-linha" onClick={() => setPaciente(p)}>
          <div className="em-linha-corpo">
            <strong>{p.nome}</strong>
            <span className="em-sub">{(p.origens || [p.origem]).map(o => ORIGEM[o]).join(' + ')} · último e-mail {quando(p.ultimo)}</span>
          </div>
          <div className="em-linha-acoes">
            {p.verificar > 0 && <span className="em-tag em-tag-amarela" title="Achados pelo nome com dúvida (homônimos)">{p.verificar} a verificar</span>}
            <span className="em-tag em-tag-verde">{p.emails} e-mail(s)</span>
          </div>
        </div>
      ))}
      {d?.encontrados?.length > 0 && <div className="em-secao">Outros pacientes com esse nome (sem e-mail ligado)</div>}
      {d?.encontrados?.map(p => (
        <div key={p.id} className="em-linha" onClick={() => setPaciente(p)}>
          <div className="em-linha-corpo"><strong>{p.nome}</strong><span className="em-sub">{ORIGEM[p.origem]}{p.convenio ? ` · ${p.convenio}` : ''}{p.emAtendimento ? ' · em atendimento' : ''}{p.status ? ` · ${p.status}` : ''}</span></div>
        </div>
      ))}
    </div>
  );
}

// ── Contatos: quem a equipe já escreveu e quem escreveu para as caixas (todo o histórico) ──
function Contatos({ onEscrever, onAviso }) {
  const [lista, setLista] = useState(null);
  const [busca, setBusca] = useState('');
  useEffect(() => { email('contatos').then(r => setLista(r.contatos || [])).catch(e => onAviso(e.message, 'erro')); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const t = busca.trim().toLowerCase();
  const filtrados = (lista || []).filter(c => !t || c.email.includes(t) || (c.nome || '').toLowerCase().includes(t)).slice(0, 300);
  return (
    <div className="em-painel">
      <div className="em-painel-cab">
        <h3><Users size={17} /> Contatos</h3>
        <span className="em-sub">Montados a partir de todo o histórico das suas caixas: para quem a equipe já escreveu e quem escreveu para vocês. Também aparecem como sugestão no "Para" ao escrever.</span>
      </div>
      <div className="em-busca"><Search size={15} /><input autoFocus placeholder="Buscar por nome ou e-mail" value={busca} onChange={e => setBusca(e.target.value)} /></div>
      {!lista && <div className="em-vazio"><Loader2 size={16} className="em-gira" /> Carregando contatos…</div>}
      {lista && !filtrados.length && <div className="em-vazio">{t ? 'Ninguém encontrado.' : 'Ainda sem contatos — o robô monta a lista na próxima leitura da caixa.'}</div>}
      {filtrados.map(c => (
        <div key={c.email} className="em-linha" onClick={() => onEscrever(c.email)} title="Escrever para este contato">
          <div className="em-linha-corpo">
            <strong>{c.nome || c.email}</strong>
            {c.nome && <span className="em-sub">{c.email}</span>}
          </div>
          <div className="em-linha-acoes"><button className="em-btn em-btn-mini" onClick={e => { e.stopPropagation(); onEscrever(c.email); }}><PenSquare size={13} /> Escrever</button></div>
        </div>
      ))}
      {lista && filtrados.length === 300 && <div className="em-vazio">Mostrando os 300 primeiros — refine a busca.</div>}
    </div>
  );
}

// ── Tela ─────────────────────────────────────────────────────────────────────────
export default function Email() {
  const largura = useLargura();
  const estreito = largura < 1000;
  const [eu, setEu] = useState(null);
  const [erro, setErro] = useState('');
  const [contaId, setContaId] = useState(() => { try { return localStorage.getItem('email_conta') || ''; } catch { return ''; } });
  const [visao, setVisao] = useState({ tipo: 'pasta', pasta: 'INBOX' });
  const [pastas, setPastas] = useState([]);
  const [aberto, setAberto] = useState(null);
  const [compor, setCompor] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [novidades, setNovidades] = useState(null);
  const [sinalLista, setSinalLista] = useState(0);
  const [sincronizando, setSincronizando] = useState(false);
  const [paciente, setPaciente] = useState(null);
  const [menuAberto, setMenuAberto] = useState(false);
  // cores escolhidas pela pessoa (botão 🎨 Cores)
  const { tema, salvar: salvarTema } = useTemaTela('email');
  const contagem = useRef(() => {});
  const ultimoUidNext = useRef({});

  const avisar = useCallback((texto, tipo = 'ok') => { if (texto === 'SESSAO_ENCERRADA') { setErro(texto); return; } setAviso({ texto, tipo }); if (tipo === 'ok') setTimeout(() => setAviso(a => (a?.texto === texto ? null : a)), 5000); }, []);

  const carregarEu = useCallback(() => email('eu').then(d => {
    setEu(d);
    setContaId(atual => (d.contas.some(c => c.id === atual) ? atual : d.contas[0]?.id || ''));
    if (!d.contas.length) setVisao({ tipo: 'caixas' });
  }).catch(e => setErro(e.message)), []);
  useEffect(() => { carregarEu(); }, [carregarEu]);

  const conta = eu?.contas.find(c => c.id === contaId);
  useEffect(() => { try { if (contaId) localStorage.setItem('email_conta', contaId); } catch { /* sem armazenamento */ } }, [contaId]);

  const carregarPastas = useCallback(() => {
    if (!conta?.temSenha) { setPastas([]); return; }
    email('pastas', { contaId: conta.id }).then(r => setPastas(r.pastas)).catch(e => avisar(e.message, 'erro'));
  }, [conta?.id, conta?.temSenha]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregarPastas(); }, [carregarPastas]);

  // novidades a cada 60 s: contagem de não lidas, prazos e reuniões; e-mail novo recarrega a lista
  useEffect(() => {
    if (!eu?.contas.length) return undefined;
    const ver = () => email('novidades').then(n => {
      setNovidades(n);
      for (const cx of n.caixas || []) {
        const antes = ultimoUidNext.current[cx.contaId];
        if (antes && cx.uidNext && cx.uidNext !== antes && cx.contaId === contaId) { setSinalLista(s => s + 1); carregarPastas(); }
        if (cx.uidNext) ultimoUidNext.current[cx.contaId] = cx.uidNext;
      }
    }).catch(() => {});
    ver();
    const t = setInterval(() => { if (!document.hidden) ver(); }, 60000);
    return () => clearInterval(t);
  }, [eu, contaId, carregarPastas]);

  const pastaAtual = pastas.find(p => p.caminho === visao.pasta);
  const ehEnviados = pastaAtual?.papel === '\\Sent' || pastaAtual?.papel === '\\Drafts';

  function irPara(v) { setVisao(v); setAberto(null); setMenuAberto(false); if (v.tipo !== 'pacientes') setPaciente(null); }
  function abrirPaciente(p) { setPaciente(p); setVisao({ tipo: 'pacientes' }); }
  async function sincronizar() {
    if (!conta) return;
    setSincronizando(true);
    try {
      const r = await email('sincronizar', { contaId: conta.id });
      if (r.motivo) avisar(r.motivo);
      else avisar(`Robô leu ${r.novos} e-mail(s) novo(s)${r.convites ? `, ${r.convites} convite(s) para a agenda` : ''}${r.prazos ? `, ${r.prazos} prazo(s)` : ''}${r.pacientes ? `, ${r.pacientes} ligação(ões) com paciente` : ''}.${r.faltam ? ` Ainda faltam ${r.faltam} — clique de novo para continuar.` : ''}`);
      await carregarEu();
      setSinalLista(s => s + 1);
    } catch (e) { avisar(e.message, 'erro'); } finally { setSincronizando(false); }
  }

  const naoLidasDe = id => novidades?.caixas?.find(c => c.contaId === id)?.naoLidas;

  if (erro === 'SESSAO_ENCERRADA') {
    return (
      <div className="em-pagina">
        <div className="em-aviso em-aviso-erro" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <strong>Sua sessão no Quality foi encerrada</strong>
          <span>Isso acontece quando você entra em outro computador ou celular. As outras telas ainda abrem, mas o E-mail precisa de uma sessão ativa.</span>
          <button className="em-btn em-btn-primario" style={{ marginTop: 8 }} onClick={async () => { try { await supabase.auth.signOut(); } catch (_) { /* segue */ } window.location.href = '/login'; }}>Sair e entrar de novo</button>
        </div>
      </div>
    );
  }
  if (erro) return <div className="em-pagina"><div className="em-aviso em-aviso-erro">{erro}</div></div>;
  if (!eu) return <div className="em-pagina"><div className="em-vazio"><Loader2 size={18} className="em-gira" /> Carregando…</div></div>;

  const cota = conta?.estado?.cota;
  const menu = (
    <nav className={`em-nav ${estreito && !menuAberto ? 'em-escondido' : ''}`}>
      {eu.contas.length > 0 && (
        <>
          {eu.contas.length > 1 ? (
            <select className="em-conta-sel" value={contaId} onChange={e => { setContaId(e.target.value); irPara({ tipo: 'pasta', pasta: 'INBOX' }); }}>
              {eu.contas.map(c => <option key={c.id} value={c.id}>{c.nome}{naoLidasDe(c.id) ? ` (${naoLidasDe(c.id)})` : ''}</option>)}
            </select>
          ) : <div className="em-conta-unica"><strong>{conta?.nome}</strong><span>{conta?.endereco}</span></div>}
          <button className="em-btn em-btn-primario em-escrever" onClick={() => setCompor({ modo: 'novo' })} disabled={!conta?.temSenha}><PenSquare size={16} /> Escrever</button>
          {cota && (
            <div className={`em-cota ${cota.pct >= 85 ? 'em-cota-alerta' : ''}`} title={`${(cota.usado / 1073741824).toFixed(1)} GB de ${(cota.limite / 1073741824).toFixed(0)} GB`}>
              <div className="em-cota-barra"><div style={{ width: `${Math.min(100, cota.pct)}%` }} /></div>
              <span><HardDrive size={11} /> Caixa {cota.pct}% cheia{cota.pct >= 85 ? ' — e-mails novos podem voltar!' : ''}</span>
            </div>
          )}
          <div className="em-nav-grupo">
            {pastas.map(p => {
              const Ic = ICONE_PASTA[p.papel] || Folder;
              return (
                <button key={p.caminho} className={visao.tipo === 'pasta' && visao.pasta === p.caminho ? 'ativa' : ''} style={{ paddingLeft: 10 + p.nivel * 14 }} onClick={() => irPara({ tipo: 'pasta', pasta: p.caminho })} title={p.caminho}>
                  <Ic size={15} /> <span className="em-nav-nome">{p.nome}</span>
                  {p.naoLidas > 0 && p.papel !== '\\Sent' && <span className="em-cont">{p.naoLidas}</span>}
                </button>
              );
            })}
          </div>
          <div className="em-nav-titulo">Organizar</div>
          <div className="em-nav-grupo">
            <button className={visao.tipo === 'reunioes' ? 'ativa' : ''} onClick={() => irPara({ tipo: 'reunioes' })}><CalendarDays size={15} /> <span className="em-nav-nome">Reuniões</span>{novidades?.reunioes > 0 && <span className="em-cont em-cont-azul">{novidades.reunioes}</span>}</button>
            <button className={visao.tipo === 'prazos' ? 'ativa' : ''} onClick={() => irPara({ tipo: 'prazos' })}><AlarmClock size={15} /> <span className="em-nav-nome">Prazos</span>{novidades?.prazosAbertos > 0 && <span className="em-cont em-cont-vermelho">{novidades.prazosAbertos}</span>}</button>
            <button className={visao.tipo === 'pacientes' ? 'ativa' : ''} onClick={() => irPara({ tipo: 'pacientes' })}><Users size={15} /> <span className="em-nav-nome">Pacientes</span></button>
            <button className={visao.tipo === 'contatos' ? 'ativa' : ''} onClick={() => irPara({ tipo: 'contatos' })}><Mail size={15} /> <span className="em-nav-nome">Contatos</span></button>
          </div>
        </>
      )}
      <div className="em-nav-rodape">
        <div className="em-nav-tema"><SeletorTema tema={tema} onSalvar={salvarTema} /></div>
        {window.Notification && Notification.permission === 'default' && (
          <button className="em-btn em-btn-mini em-avisos-btn" onClick={() => Notification.requestPermission().then(p => avisar(p === 'granted' ? 'Pronto: você vai receber um aviso no computador quando chegar e-mail.' : 'Avisos do computador não liberados (dá para liberar no cadeado da barra de endereço).', p === 'granted' ? 'ok' : 'erro'))}>🔔 Ativar avisos no computador</button>
        )}
        <button className={visao.tipo === 'caixas' ? 'ativa' : ''} onClick={() => irPara({ tipo: 'caixas' })}><Settings2 size={15} /> Caixas</button>
        {conta && (
          <div className="em-sync">
            <button className="em-btn em-btn-mini" onClick={sincronizar} disabled={sincronizando || !conta.temSenha}>
              <RefreshCw size={13} className={sincronizando ? 'em-gira' : ''} /> {sincronizando ? 'Lendo…' : 'Atualizar agora'}
            </button>
            <span className="em-sub">{conta.estado?.ultimaLeitura ? `Robô: ${dataHora(conta.estado.ultimaLeitura)}` : 'Robô ainda não leu esta caixa'}</span>
            {conta.estado?.faltam > 0 && <span className="em-sub">Primeira leitura: faltam {conta.estado.faltam}</span>}
            {conta.estado?.erro && <span className="em-sub em-cor-erro" title={conta.estado.erro}>Erro: {conta.estado.erro.slice(0, 80)}</span>}
            {!conta.temSenha && <span className="em-sub em-cor-erro">Senha desta caixa não configurada aqui.</span>}
          </div>
        )}
      </div>
    </nav>
  );

  let meio = null;
  if (visao.tipo === 'caixas') meio = <Caixas eu={eu} onMudou={() => { carregarEu(); carregarPastas(); }} />;
  else if (!conta) meio = <div className="em-vazio">Escolha uma caixa.</div>;
  else if (visao.tipo === 'pasta') meio = <ListaPasta conta={conta} pasta={visao.pasta} pastas={pastas} ehEnviados={ehEnviados} aberto={aberto} onAbrir={setAberto} recarregarSinal={sinalLista} onAviso={avisar} onContagem={contagem} />;
  else if (visao.tipo === 'reunioes') meio = <Reunioes eu={eu} onAbrir={setAberto} onAviso={avisar} sinal={sinalLista} />;
  else if (visao.tipo === 'prazos') meio = <Prazos onAbrir={setAberto} onAviso={avisar} onAbrirPaciente={abrirPaciente} sinal={sinalLista} />;
  else if (visao.tipo === 'contatos') meio = <Contatos onAviso={avisar} onEscrever={end => setCompor({ modo: 'novo', inicial: { para: [end] } })} />;
  else if (visao.tipo === 'pacientes') meio = <Pacientes paciente={paciente} setPaciente={setPaciente} onAbrir={setAberto} onAviso={avisar} aberto={aberto} />;

  // e-mail clicado abre a conversa numa janela por cima (como a de escrever), com o histórico
  const conversa = aberto && (
    <Conversa alvo={aberto} eu={{ ...eu, caixa: conta?.endereco }} pastas={pastas} onAviso={avisar}
      onFechar={() => setAberto(null)}
      onMudou={() => { setSinalLista(s => s + 1); carregarPastas(); }}
      onAbrirPaciente={p => { setAberto(null); abrirPaciente(p); }}
      onResponder={(modo, original) => setCompor({ modo, original })} />
  );

  return (
    <div className="em-tela" style={{ '--em-verde': tema.destaque, '--em-tela-fundo': tema.fundo, '--em-nav-texto': textoPara(tema.fundo) }}>
      {aviso && <div className={`em-aviso em-aviso-fixo em-aviso-${aviso.tipo}`}>{aviso.texto}<button onClick={() => setAviso(null)}><X size={14} /></button></div>}
      {estreito && (
        <div className="em-topo-movel">
          <button className="em-icone" onClick={() => setMenuAberto(a => !a)}><Inbox size={18} /></button>
          <strong>{visao.tipo === 'pasta' ? (pastaAtual?.nome || 'E-mail') : { reunioes: 'Reuniões', prazos: 'Prazos', pacientes: 'Pacientes', contatos: 'Contatos', caixas: 'Caixas' }[visao.tipo]}</strong>
          {conta?.temSenha && <button className="em-icone em-dir" onClick={() => setCompor({ modo: 'novo' })}><PenSquare size={18} /></button>}
        </div>
      )}
      <div className="em-colunas">
        {menu}
        <div className="em-meio">{meio}</div>
      </div>
      {conversa}
      {compor && eu.contas.length > 0 && (
        <Compor contas={eu.contas.filter(c => c.temSenha)} contaId={contaId} modo={compor.modo} original={compor.original} inicial={compor.inicial}
          onFechar={() => setCompor(null)} onEnviado={() => { avisar('E-mail enviado.'); setSinalLista(s => s + 1); carregarPastas(); }} />
      )}
    </div>
  );
}
